/**
 * Accessibility enhancements layered on top of the Prestige theme JS (theme.js is left untouched so theme updates
 * stay easy to merge).
 *
 * 1. Auto-rotating carousels (WCAG 2.2.2 Pause, Stop, Hide)
 *    - pause/play button (snippets/autoplay-toggle.liquid), which also pauses slideshow videos
 *    - rotation pauses while keyboard focus is inside the carousel
 *    - rotation starts paused when the visitor asks for reduced motion
 *
 * 2. Click-to-play videos (WCAG 2.1.1 Keyboard)
 *    - <video-media> only listens for clicks, so it gets button semantics and Enter / Space support
 *    - the video section's overlay play button forwards activation to its video
 */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const AUTOPLAY_CAROUSELS = ['slideshow-carousel', 'announcement-bar-carousel', 'testimonial-carousel'];

/* ------------------------------------------------------------------------------------------------------------------
 * 1. AUTOPLAY CAROUSELS
 * ------------------------------------------------------------------------------------------------------------------ */

function setupAutoplayCarousel(carousel) {
  if (carousel.hasAttribute('data-a11y-autoplay')) {
    return;
  }

  carousel.setAttribute('data-a11y-autoplay', '');

  const player = carousel.player; // Only exists when the carousel has autoplay and more than one slide
  const toggles = carousel.id ? Array.from(document.querySelectorAll(`[data-action="toggle-autoplay"][aria-controls="${carousel.id}"]`)) : [];
  const region = carousel.closest('.announcement-bar') || carousel;

  let userPaused = reducedMotion.matches;
  let interacting = false;
  let running = null;

  const videos = () => Array.from(carousel.querySelectorAll('video'));

  const playSelectedVideos = () => {
    (carousel.selectedCell || carousel).querySelectorAll('video').forEach((video) => {
      if (video.offsetParent) {
        video.play().catch(() => {});
      }
    });
  };

  const update = () => {
    const shouldRun = !userPaused && !interacting;

    if (player && shouldRun !== running) {
      shouldRun ? player.start() : player.stop();
    }

    running = shouldRun;

    toggles.forEach((toggle) => {
      toggle.querySelector('.autoplay-toggle__pause').hidden = userPaused;
      toggle.querySelector('.autoplay-toggle__play').hidden = !userPaused;
    });
  };

  toggles.forEach((toggle) => {
    toggle.addEventListener('click', () => {
      userPaused = !userPaused;

      if (userPaused) {
        videos().forEach((video) => video.pause());
      } else {
        // The visitor is using the button itself, so the focus pause should not block the restart
        interacting = false;
        playSelectedVideos();
      }

      update();
    });
  });

  // The theme restarts videos when a slide changes, and videos can autoplay as they load: keep them paused if the
  // visitor paused the carousel
  carousel.addEventListener('play', (event) => userPaused && event.target.pause(), { capture: true });
  carousel.addEventListener('carousel:settle', () => userPaused && videos().forEach((video) => video.pause()));

  const isToggle = (element) => element instanceof Element && element.closest('[data-action="toggle-autoplay"]');

  // Keyboard focus only: a mouse click on a dot also focuses it, and that should not stop the rotation
  region.addEventListener('focusin', (event) => {
    if (!isToggle(event.target) && event.target.matches(':focus-visible')) {
      interacting = true;
      update();
    }
  });

  region.addEventListener('focusout', (event) => {
    if (!region.contains(event.relatedTarget) || isToggle(event.relatedTarget)) {
      interacting = false;
      update();
    }
  });

  reducedMotion.addEventListener('change', (event) => {
    userPaused = event.matches;
    update();
  });

  if (userPaused) {
    videos().forEach((video) => video.pause());
  }

  // Leave the initial start to the theme (it waits for the carousel to be visible) unless autoplay must be stopped
  if (userPaused) {
    update();
  } else {
    running = true;
  }
}

function setupAutoplayCarousels(root = document) {
  AUTOPLAY_CAROUSELS.forEach((tagName) => {
    customElements.whenDefined(tagName).then(() => {
      root.querySelectorAll(`${tagName}[autoplay], ${tagName}:has(video)`).forEach(setupAutoplayCarousel);
    });
  });
}

/* ------------------------------------------------------------------------------------------------------------------
 * 2. CLICK-TO-PLAY VIDEOS
 * ------------------------------------------------------------------------------------------------------------------ */

const playVideoLabel = window.themeVariables?.strings?.playVideo || 'Play video';

function focusPlayer(videoMedia) {
  const tryFocus = () => {
    const player = videoMedia.querySelector('video[controls], iframe');
    player?.focus();
    return Boolean(player);
  };

  if (!tryFocus()) {
    // YouTube and Vimeo iframes are only added to the DOM once playback is requested
    const observer = new MutationObserver(() => tryFocus() && observer.disconnect());
    observer.observe(videoMedia, { childList: true });
    setTimeout(() => observer.disconnect(), 5000);
  }
}

function startVideo(videoMedia) {
  videoMedia.play();

  // A native video without controls cannot be paused any other way, so the host stays a play/pause toggle
  if (videoMedia.querySelector('video:not([controls])')) {
    return;
  }

  videoMedia.removeAttribute('role');
  videoMedia.removeAttribute('aria-label');
  videoMedia.removeAttribute('tabindex');
  focusPlayer(videoMedia);
}

function setupVideoMedia(videoMedia) {
  if (videoMedia.hasAttribute('autoplay') || videoMedia.hasAttribute('data-a11y-video')) {
    return;
  }

  videoMedia.setAttribute('data-a11y-video', '');

  // The video section renders its own play button over the video, which is handled by the delegated listener below
  if (videoMedia.parentElement?.querySelector('.play-button')) {
    return;
  }

  videoMedia.tabIndex = 0;
  videoMedia.setAttribute('role', 'button');
  videoMedia.setAttribute('aria-label', playVideoLabel);
  videoMedia.setAttribute('aria-pressed', 'false');

  new MutationObserver(() => videoMedia.setAttribute('aria-pressed', String(videoMedia.hasAttribute('playing'))))
    .observe(videoMedia, { attributes: true, attributeFilter: ['playing'] });

  videoMedia.addEventListener('keydown', (event) => {
    if (event.target !== videoMedia || (event.key !== 'Enter' && event.key !== ' ')) {
      return;
    }

    event.preventDefault();
    videoMedia.hasAttribute('playing') ? videoMedia.pause() : startVideo(videoMedia);
  });
}

function setupVideoMedias(root = document) {
  customElements.whenDefined('video-media').then(() => {
    root.querySelectorAll('video-media').forEach(setupVideoMedia);
  });
}

// Video section: the overlay button has pointer-events: none (mouse clicks fall through to the video) but it can still
// be focused and activated from the keyboard, which used to do nothing
document.addEventListener('click', (event) => {
  const button = event.target.closest('.content-over-media .play-button');
  const videoMedia = button && Array.from(button.closest('.content-over-media').querySelectorAll(':scope > video-media')).find((item) => item.offsetParent);

  if (videoMedia && !videoMedia.hasAttribute('playing')) {
    startVideo(videoMedia);
  }
});

/* ------------------------------------------------------------------------------------------------------------------
 * 3. FORM ERRORS (WCAG 3.3.1 Error Identification, 4.1.3 Status Messages)
 *
 * Error banners rendered with "error_fields" (snippets/banner.liquid) list the names of the fields Shopify rejected,
 * such as "email" or "password" ("form" means the whole form, e.g. a wrong login). Mark those fields as invalid, point
 * them to the message, and move focus to the first message so it is read out after the page reloads.
 * ------------------------------------------------------------------------------------------------------------------ */

let errorBannerCount = 0;

function setupFormErrors() {
  const banners = Array.from(document.querySelectorAll('.banner--error[data-error-fields]'));

  banners.forEach((banner) => {
    const form = banner.closest('form');

    if (!form) {
      return;
    }

    banner.id ||= `form-error-${++errorBannerCount}`;

    const fieldNames = banner.getAttribute('data-error-fields').trim().split(/\s+/).filter(Boolean);
    const inputs = Array.from(form.querySelectorAll('input:not([type="hidden"]), textarea, select'));

    const invalidInputs = fieldNames.includes('form')
      ? inputs.filter((input) => input.required)
      : inputs.filter((input) => fieldNames.some((field) => input.name === field || input.name.endsWith(`[${field}]`)));

    invalidInputs.forEach((input) => {
      const describedBy = (input.getAttribute('aria-describedby') || '').split(' ').filter(Boolean);

      input.setAttribute('aria-invalid', 'true');
      input.setAttribute('aria-describedby', [...new Set([...describedBy, banner.id])].join(' '));

      input.addEventListener('input', () => input.removeAttribute('aria-invalid'), { once: true });
    });
  });

  // Hidden banners (e.g. the password recovery form while the login form is shown) cannot take focus
  banners.find((banner) => banner.offsetParent)?.focus();
}

/* ------------------------------------------------------------------------------------------------------------------
 * INIT
 * ------------------------------------------------------------------------------------------------------------------ */

setupAutoplayCarousels();
setupVideoMedias();
setupFormErrors();

document.addEventListener('shopify:section:load', (event) => {
  setupAutoplayCarousels(event.target);
  setupVideoMedias(event.target);
});
